import {
  type ReactNode,
  type MouseEvent,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Rnd, type RndDragCallback, type RndResizeCallback } from "react-rnd";

import { createPortal, flushSync } from "react-dom";

import type {
  DockMode,
  FloatingWindowLayout,
  FloatingWindowPosition,
  FloatingWindowSize,
} from "../../types/floatingWindow";

import { FloatingWindowControls } from "./FloatingWindowControls";
import { FloatingWindowHeader } from "./FloatingWindowHeader";
import {
  getInitialFloatingLayout,
  clampPositionToViewport,
  getDockLayout,
  getSnapDockMode,
  getResizeConfig,
  canUseDOM,
  DEFAULT_HEADER_HEIGHT,
  getDockedResizeLayout,
  getFloatingWindowBoundsElement,
  getUndockedLayout,
  MIN_WINDOW_WIDTH,
} from "../../utils/floatingWindow";

type Props = {
  open: boolean;
  title: string;
  icon?: string;
  children: ReactNode;
  onClose: () => void;
  defaultWidth?: number;
  defaultHeight?: number;
};

export const FloatingWindow = ({
  open,
  title,
  icon,
  children,
  onClose,
  defaultWidth = 900,
  defaultHeight = 700,
}: Props) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const dragStartedDockedRef = useRef(false);
  const boundsElement = canUseDOM() ? getFloatingWindowBoundsElement() : null;

  const initialFloatingLayout = useMemo(
    () => getInitialFloatingLayout(defaultWidth, defaultHeight),
    [defaultWidth, defaultHeight],
  );

  const [dragging, setDragging] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(DEFAULT_HEADER_HEIGHT);
  const [dockMode, setDockMode] = useState<DockMode>("floating");
  const [lastDockMode, setLastDockMode] = useState<DockMode>("right");
  const [position, setPosition] = useState<FloatingWindowPosition>(
    initialFloatingLayout.position,
  );
  const [size, setSize] = useState<FloatingWindowSize>(
    initialFloatingLayout.size,
  );
  const [lastFloatingLayout, setLastFloatingLayout] =
    useState<FloatingWindowLayout>(initialFloatingLayout);

  useLayoutEffect(() => {
    if (!headerRef.current || typeof ResizeObserver === "undefined") {
      return;
    }

    const element = headerRef.current;

    const updateHeaderHeight = () => {
      setHeaderHeight(element.offsetHeight);
    };

    updateHeaderHeight();

    const observer = new ResizeObserver(updateHeaderHeight);

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!dragging || !canUseDOM()) {
      return;
    }

    const previousUserSelect = document.body.style.userSelect;

    document.body.style.userSelect = "none";

    return () => {
      document.body.style.userSelect = previousUserSelect;
    };
  }, [dragging]);

  useLayoutEffect(() => {
    if (!open || !boundsElement) {
      return;
    }

    const syncLayoutToBounds = () => {
      if (dockMode === "floating") {
        setPosition((prevPosition) => {
          const nextPosition = clampPositionToViewport(prevPosition, size);

          if (
            nextPosition.x === prevPosition.x &&
            nextPosition.y === prevPosition.y
          ) {
            return prevPosition;
          }

          return nextPosition;
        });

        return;
      }

      const layout = getDockLayout(dockMode, size);

      if (layout) {
        setPosition(layout.position);
        setSize(layout.size);
      }
    };

    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(syncLayoutToBounds);

    observer?.observe(boundsElement);
    window.addEventListener("resize", syncLayoutToBounds);

    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", syncLayoutToBounds);
    };
  }, [open, dockMode, size, boundsElement]);

  if (!open || !canUseDOM()) {
    return null;
  }

  const applyDockMode = (
    nextDockMode: Exclude<DockMode, "floating">,
    preferredSize?: FloatingWindowSize,
  ) => {
    const layout = getDockLayout(nextDockMode, preferredSize);

    if (!layout) {
      return;
    }

    setDockMode(nextDockMode);
    setLastDockMode(nextDockMode);
    setPosition(layout.position);
    setSize(layout.size);
  };

  const restoreFloatingMode = () => {
    setDockMode("floating");

    const restoredPosition = clampPositionToViewport(
      lastFloatingLayout.position,
      lastFloatingLayout.size,
    );

    setPosition(restoredPosition);
    setSize(lastFloatingLayout.size);
  };

  const handleToggleDock = () => {
    if (minimized) {
      return;
    }

    if (dockMode === "floating") {
      applyDockMode(lastDockMode);
      return;
    }

    restoreFloatingMode();
  };

  const handleToggleMinimize = () => {
    setMinimized((prev) => !prev);
  };

  const handleHeaderMouseDownCapture = (event: MouseEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest(".floating-window-controls")) {
      return;
    }

    if (minimized || dockMode === "floating") {
      return;
    }

    const floatingLayout = getUndockedLayout(
      dockMode,
      lastFloatingLayout,
      defaultWidth,
      defaultHeight,
    );

    dragStartedDockedRef.current = true;

    flushSync(() => {
      setDockMode("floating");
      setPosition(floatingLayout.position);
      setSize(floatingLayout.size);
    });
  };

  const handleDragStart = () => {
    setDragging(true);
    dragStartedDockedRef.current =
      dragStartedDockedRef.current || dockMode !== "floating";

    if (dockMode === "floating") {
      return;
    }

    const floatingLayout = getUndockedLayout(
      dockMode,
      lastFloatingLayout,
      defaultWidth,
      defaultHeight,
    );

    setDockMode("floating");
    setPosition(floatingLayout.position);
    setSize(floatingLayout.size);
  };

  const handleDragStop: RndDragCallback = (_, data) => {
    setDragging(false);

    const nextPosition = {
      x: data.x,
      y: data.y,
    };

    setPosition(nextPosition);

    if (minimized) {
      setLastFloatingLayout({
        position: nextPosition,
        size,
      });

      dragStartedDockedRef.current = false;
      return;
    }

    if (dragStartedDockedRef.current) {
      setLastFloatingLayout({
        position: nextPosition,
        size,
      });

      dragStartedDockedRef.current = false;
      return;
    }

    const snapDockMode = getSnapDockMode(nextPosition, size);

    if (snapDockMode) {
      applyDockMode(snapDockMode);
      dragStartedDockedRef.current = false;
      return;
    }

    if (dockMode === "floating") {
      setLastFloatingLayout({
        position: nextPosition,
        size,
      });
    }

    dragStartedDockedRef.current = false;
  };

  const handleResizeStop: RndResizeCallback = (
    _,
    __,
    ref,
    ___,
    nextPosition,
  ) => {
    const nextSize = {
      width: ref.offsetWidth,
      height: ref.offsetHeight,
    };

    setSize(nextSize);
    setPosition(nextPosition);

    if (dockMode !== "floating") {
      const layout = getDockedResizeLayout(dockMode, nextSize);
      if (layout) {
        setPosition(layout.position);
        setSize(layout.size);
      }

      return;
    }

    setLastFloatingLayout({
      position: nextPosition,
      size: nextSize,
    });
  };

  const rndSize = {
    width: minimized ? "auto" : size.width,
    height: minimized ? headerHeight : size.height,
  };

  const controls = (
    <FloatingWindowControls
      minimized={minimized}
      dockMode={dockMode}
      onToggleDock={handleToggleDock}
      onToggleMinimize={handleToggleMinimize}
      onClose={onClose}
    />
  );

  return createPortal(
    <Rnd
      size={rndSize}
      position={position}
      bounds={boundsElement ?? "window"}
      minWidth={minimized ? undefined : MIN_WINDOW_WIDTH}
      minHeight={headerHeight}
      dragHandleClassName="floating-window-header"
      cancel=".floating-window-controls, .floating-window-controls *"
      onDragStart={handleDragStart}
      onDragStop={handleDragStop}
      onResizeStop={handleResizeStop}
      enableResizing={getResizeConfig(minimized, dockMode)}
      style={{
        position: "fixed",
        zIndex: 9999,
        willChange: "transform",
      }}
    >
      <div
        className={`flex h-full flex-col overflow-hidden rounded-xl border border-slate-300 bg-white ${
          dragging ? "opacity-95 shadow-lg" : "shadow-2xl"
        }`}
        style={{
          transform: "translateZ(0)",
        }}
      >
        <FloatingWindowHeader
          ref={headerRef}
          title={title}
          icon={icon}
          controls={controls}
          onMouseDownCapture={handleHeaderMouseDownCapture}
        />

        {!minimized && (
          <div className="min-h-0 flex-1 overflow-hidden">{children}</div>
        )}
      </div>
    </Rnd>,
    document.body,
  );
};
